import axios from 'axios';

import {IPublication, Publication} from '@/shared/model/publication.model';
import DataService from '@/core/base/service/data.service';
import {ITag} from '@/shared/model/tag.model';
import {PaginationQuery} from "@/shared/sort/pagination-query.interface";
import {DataResult} from "@/core/base/type/data-result.type";

export default class PublicationService extends DataService<Publication> {
    public baseApiUrl: string = 'api/publications';

    public retrieveForCampaignLink(campaignLink: string, paginationQuery?: PaginationQuery): Promise<DataResult<Publication>> {
        return this.retrieveWithUrl(`api/shared/${campaignLink}/publications`, paginationQuery);
    }

    public retrieveForCampaign(campaignId: number, paginationQuery?: PaginationQuery): Promise<DataResult<Publication>> {
        return this.retrieveWithUrl(`${this.baseApiUrl}/campaign/${campaignId}`, paginationQuery);
    }

    public setAssignment(
        publication: IPublication | IPublication[],
        campaignId: number,
        mode: 'assign' | 'remove'
    ): Promise<IPublication[]> {
        const publications: IPublication[] = Array.isArray(publication) ? publication : [publication];
        const publicationIds: string = publications.map(p => p.id).join(',');
        return new Promise<any>((resolve, reject) => {
            axios
                .put(this.baseApiUrl + `/${publicationIds}/${mode}/${campaignId}`)
                .then(res => {
                    if (mode === 'assign') {
                        publications.forEach(publication => {
                            Object.assign(publication, res.data.find(p => p.id === publication.id) ?? {});
                        });
                    }
                    resolve(res.data);
                })
                .catch(err => {
                    this.errorStore.handleApiError(err);
                    reject(err);
                });
        });
    }

    public discardForCampaign(campaignId: number): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            axios
                .put(this.baseApiUrl + `/discard-for-campaign/${campaignId}`)
                .then(res => resolve())
                .catch(err => {
                    this.errorStore.handleApiError(err);
                    reject(err);
                });
        });
    }

    public discardSuggestion(publicationId: number): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            axios
                .put(this.baseApiUrl + `/${publicationId}/discard-suggestion`)
                .then(res => resolve(res.data))
                .catch(err => {
                    this.errorStore.handleApiError(err);
                    reject(err);
                });
        });
    }

    public refresh(ids: number[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            axios
                .put(this.baseApiUrl + `/refresh`, ids)
                .then(res => resolve())
                .catch(err => {
                    this.errorStore.handleApiError(err);
                    reject(err);
                });
        });
    }

    public updateAssignmentTags(assignmentId: number, tags: ITag[]) {
        return new Promise<void>((resolve, reject) => {
            axios
                .put(this.baseApiUrl + `/updateTags`, {
                    assignmentId,
                    tagIds: tags.map(t => t.id),
                })
                .then(res => resolve())
                .catch(err => {
                    this.errorStore.handleApiError(err);
                    reject(err);
                });
        });
    }

    public markAsSponsored(publications: IPublication[], sponsored: boolean): Promise<IPublication[]> {
        const publicationIds: string = publications.map(p => p.id).join(',');
        return new Promise<Publication[]>((resolve, reject) => {
            axios
                .put(`${this.baseApiUrl}/${publicationIds}/markAsSponsored`, {
                    sponsored,
                })
                .then(res => {
                    publications.forEach(publication => {
                        Object.assign(
                            publication,
                            res.data.find(p => p.id === publication.id)
                        );
                    });
                    resolve(res.data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    }

    public static shorten({message, shareMessage}: Publication): string {
        if (!message) {
            return '';
        }
        let completeMessage: string = message;
        if (shareMessage) {
            completeMessage = `${message} <i>(${shareMessage}`;
        }
        if (completeMessage.length >= 100) {
            completeMessage = completeMessage.substr(0, 100) + ' ...';
        }
        if (shareMessage && message.length < 100) {
            return `${completeMessage})</i>`;
        } else {
            return completeMessage;
        }
    }
}
